import { z } from "zod";
import { Id, IsoDate, Page, PageQuery } from "./common.ts";
import { AgentMode, AgentSource, AiLabels, Channel, ConversationAgent, FlagKey, ResolvedAgent, type AgentSource as AgentSourceT, type ResolvedAgent as ResolvedAgentT } from "./domain.ts";
import type { RouteDef } from "./routes.ts";

/**
 * AI agents (ALP-83): personas the org configures to answer comments and DMs.
 *
 * - An agent = name/emoji + a versioned config (persona, tone, instructions, do/don't, language, reply style per surface,
 *   model alias, temperature, knowledge datasets, handoff rules, working hours, limits) + mode (draft for approval or
 *   auto send). Every save is a new version; every reply records the agent id + version, tokens and cost.
 * - Assignment (resolution order, first match wins, see `resolveAgent`): a rule action (`reply_with_agent` /
 *   `dm_with_agent`) → the conversation's manual assignment (or "off") → the post's agent → the account's default for
 *   the surface → the channel's default (e.g. every Instagram account).
 * - Runtime (worker): prompt = agent config + conversation context + knowledge snippets (SemanticIndex / Dify Knowledge),
 *   through the AI router (task `agent-responder`: direct LiteLLM by default, Dify app `malfak-responder` optional).
 *   Messaging policy, plan (ai_replies + agent_replies), the agent's limits, working hours and handoff are checked
 *   first; then an approval (draft) or a send through the normal command path (idempotency key per event).
 */

// ---------------------------------------------------------------- vocabulary
export { AgentMode, AgentSource, ResolvedAgent };
export const AGENT_SURFACES = ["comment_public", "comment_private", "dm"] as const;
export const AgentSurface = z.enum(AGENT_SURFACES);
export type AgentSurfaceT = (typeof AGENT_SURFACES)[number];
/** Which kind of item an assignment covers. */
export const AssignmentSurface = z.enum(["comments", "dms", "all"]);
export const surfaceKind = (s: AgentSurfaceT): "comments" | "dms" => (s === "dm" ? "dms" : "comments");

/** `auto` = reply in the customer's language; Arabic dialects steer wording (Gulf, Levantine, Egyptian, Maghrebi, MSA). */
export const AGENT_LANGUAGES = ["auto", "ar", "ar-gulf", "ar-levant", "ar-egypt", "ar-maghreb", "ar-msa", "en"] as const;
export const AgentLanguage = z.enum(AGENT_LANGUAGES);
export const AGENT_TONES = ["friendly", "professional", "playful", "luxury", "empathetic", "concise", "custom"] as const;
export const AgentTone = z.enum(AGENT_TONES);
/** LiteLLM aliases an agent may use (never a provider model name). */
export const AGENT_MODELS = ["malfak-smart", "malfak-fast"] as const;
/**
 * The agent's model as a REF: `platform:<alias>` (a Malfak LiteLLM alias) or `org:<connectionModelId>` (the org's own
 * model, BYOK, ALP-86: resolved server-side to model + key while the org's `byok` feature is on, else the platform
 * model). A bare alias (`malfak-smart`, older configs) means `platform:<alias>`.
 */
export const DEFAULT_AGENT_MODEL = "platform:malfak-smart";
export const AgentModel = z.string().regex(/^(platform:[a-z0-9][a-z0-9-]{0,40}|org:[0-9a-f-]{36}|malfak-[a-z0-9-]{1,30})$/, "platform:<alias> or org:<model id>");
/** The ref for a platform alias. */
export const platformModelRef = (alias: string) => `platform:${alias}`;
/**
 * LiteLLM alias to call for a ref, until org refs are resolved by the AI routing service (ALP-86): platform refs and
 * bare aliases → the alias; org refs → the default platform model.
 */
export function agentModelAlias(ref: string | null | undefined): string {
  if (!ref) return "malfak-smart";
  if (ref.startsWith("platform:")) return ref.slice("platform:".length);
  if (ref.startsWith("org:")) return "malfak-smart";
  return ref;
}
/** The org's Dify Knowledge datasets (SemanticIndex): brand/FAQ docs the owner uploads, and past content (comments, DMs, captions). */
export const AGENT_DATASETS = ["brand", "content"] as const;
export const AgentDataset = z.enum(AGENT_DATASETS);
export const AgentStatus = z.enum(["active", "paused"]);
const HHMM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM");

export const AgentSurfaceStyle = z.object({
  /** The agent answers on this surface at all. */
  enabled: z.boolean(),
  maxLength: z.number().int().min(20).max(2200),
  /** Free-form style notes for this surface ("one sentence, invite to DM for prices"). */
  style: z.string().max(500).default(""),
  emoji: z.enum(["none", "few", "many"]).default("few"),
});
export const AgentHandoff = z.object({
  /** Any of these words (case/diacritic-insensitive) → a human takes over. */
  keywords: z.array(z.string().trim().min(1).max(60)).max(50).default([]),
  /** Built-in intents that go to a human (e.g. complaint). */
  intents: z.array(AiLabels.shape.intent.unwrap()).max(9).default([]),
  /** Custom AI categories that go to a human. */
  categories: z.array(FlagKey).max(20).default([]),
  /** Negative sentiment goes to a human. */
  negative: z.boolean().default(false),
  /** The model itself may decide to hand off when unsure (returned as `handoff: true`). */
  whenUnsure: z.boolean().default(true),
  /** Assign the item to this member when handing off (null = leave unassigned). */
  assignTo: Id.nullable().default(null),
  /** Notify owners/admins (in-app) on handoff. */
  notify: z.boolean().default(true),
});
export const AgentWorkingHours = z.object({
  enabled: z.boolean(),
  timezone: z.string().min(1).max(64),
  /** Open windows per weekday (0 = Sunday); a day without a window is closed. `to` < `from` wraps past midnight. */
  windows: z.array(z.object({ day: z.number().int().min(0).max(6), from: HHMM, to: HHMM })).max(21),
  /** Outside the hours: skip (nobody answers) or draft for approval even when the agent auto-sends. */
  outside: z.enum(["skip", "draft"]).default("skip"),
});
export const AgentLimits = z.object({
  /** Max replies (drafts + sends) per rolling hour / calendar day; null = no cap. */
  perHour: z.number().int().min(1).max(10_000).nullable().default(30),
  perDay: z.number().int().min(1).max(100_000).nullable().default(300),
  /** Max replies per conversation (DMs) or per comment thread per day. */
  perConversation: z.number().int().min(1).max(100).nullable().default(5),
  /** Share of the org's monthly AI budget (ai_budget_usd) this agent may spend, in percent; null = no own cap. */
  budgetSharePct: z.number().min(1).max(100).nullable().default(null),
});
export const AgentKnowledge = z.object({
  datasets: z.array(AgentDataset).max(2).default(["brand"]),
  maxSnippets: z.number().int().min(0).max(8).default(4),
  minScore: z.number().min(0).max(1).default(0.15),
});
export const AgentExample = z.object({ message: z.string().trim().min(1).max(500), reply: z.string().trim().min(1).max(1000) });

/** Everything that shapes an agent's answers; a new version is stored on every save. */
export const AgentConfig = z.object({
  persona: z.string().max(1000).default(""),
  tone: AgentTone.default("friendly"),
  /** The system prompt: what the agent does, what it knows, how it behaves. */
  instructions: z.string().max(8000).default(""),
  dos: z.array(z.string().trim().min(1).max(200)).max(30).default([]),
  donts: z.array(z.string().trim().min(1).max(200)).max(30).default([]),
  language: AgentLanguage.default("auto"),
  surfaces: z.object({ comment_public: AgentSurfaceStyle, comment_private: AgentSurfaceStyle, dm: AgentSurfaceStyle }),
  model: AgentModel.default(DEFAULT_AGENT_MODEL),
  temperature: z.number().min(0).max(1.5).default(0.4),
  knowledge: AgentKnowledge.default({ datasets: ["brand"], maxSnippets: 4, minScore: 0.15 }),
  handoff: AgentHandoff.default({ keywords: [], intents: [], categories: [], negative: false, whenUnsure: true, assignTo: null, notify: true }),
  workingHours: AgentWorkingHours.nullable().default(null),
  limits: AgentLimits.default({ perHour: 30, perDay: 300, perConversation: 5, budgetSharePct: null }),
  examples: z.array(AgentExample).max(10).default([]),
});
export type AgentConfigT = z.output<typeof AgentConfig>;
export type AgentConfigInput = z.input<typeof AgentConfig>;

export const DEFAULT_AGENT_CONFIG: AgentConfigT = AgentConfig.parse({
  surfaces: {
    comment_public: { enabled: true, maxLength: 300, style: "", emoji: "few" },
    comment_private: { enabled: false, maxLength: 1000, style: "", emoji: "few" },
    dm: { enabled: true, maxLength: 1000, style: "", emoji: "few" },
  },
});

// ---------------------------------------------------------------- agents
export const AgentUsage = z.object({
  repliesToday: z.number().int(),
  repliesMonth: z.number().int(),
  sentMonth: z.number().int(),
  draftsPending: z.number().int(),
  skippedMonth: z.number().int(),
  handoffsMonth: z.number().int(),
  tokensMonth: z.number(),
  costUsdMonth: z.number(),
  /** This agent's own monthly budget (budgetSharePct × the org's ai_budget_usd); null = no own cap. */
  budgetUsd: z.number().nullable(),
  limits: AgentLimits,
});
export const Agent = z.object({
  id: Id,
  name: z.string().min(1).max(60),
  emoji: z.string().max(16).nullable(),
  avatarUrl: z.string().nullable(),
  description: z.string().max(300),
  status: AgentStatus,
  mode: AgentMode,
  config: AgentConfig,
  version: z.number().int(),
  createdBy: Id.nullable(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
  /** List reads: where it is used (counts) + this month's usage. */
  assignmentCount: z.number().int().optional(),
  usage: AgentUsage.optional(),
});
export const AgentInput = z.object({
  name: z.string().trim().min(1).max(60),
  emoji: z.string().max(16).nullable().default("🤖"),
  avatarUrl: z.string().max(2000).nullable().optional(),
  description: z.string().max(300).default(""),
  status: AgentStatus.default("active"),
  mode: AgentMode.default("draft_for_approval"),
  config: AgentConfig,
  /** Optimistic concurrency on update: 409 when the agent changed since this version. */
  version: z.number().int().optional(),
  /** Short note stored with the new version ("tighter tone"). */
  note: z.string().max(200).optional(),
});
export const AgentVersion = z.object({
  version: z.number().int(),
  name: z.string(),
  emoji: z.string().nullable(),
  status: AgentStatus,
  mode: AgentMode,
  config: AgentConfig,
  note: z.string().nullable(),
  createdBy: Id.nullable(),
  createdAt: IsoDate,
});

// ---------------------------------------------------------------- assignments
/** Defaults hang on a channel (every account of that channel), an account or a post; conversations use `updateConversation`. */
export const AssignmentTarget = z.enum(["channel", "account", "post"]);
export const AgentAssignment = z.object({
  id: Id,
  agentId: Id,
  targetType: AssignmentTarget,
  /** Channel name (`instagram`) for channel defaults, else the account / post id. */
  targetRef: z.string(),
  surface: AssignmentSurface,
  label: z.string().nullable(),
  createdAt: IsoDate,
});
export const AgentAssignmentInput = z.object({ targetType: AssignmentTarget, targetRef: z.string().min(1).max(64), surface: AssignmentSurface.default("all") });
/** "Which accounts, posts, conversations and rules use this agent" (agent page). */
export const AgentOverview = z.object({
  assignments: z.array(AgentAssignment),
  conversations: z.array(z.object({ id: Id, label: z.string(), accountId: Id })).max(50),
  conversationCount: z.number().int(),
  rules: z.array(z.object({ id: Id, name: z.string(), isActive: z.boolean() })),
});

// ---------------------------------------------------------------- runs (activity log)
export const AgentRunStatus = z.enum(["drafted", "sent", "skipped", "handoff", "failed"]);
/** Stable reasons for skipped/handoff/failed (the UI localizes them). */
export const AGENT_SKIP_REASONS = [
  "agent_paused", "surface_disabled", "outside_hours", "limit_hour", "limit_day", "limit_conversation", "budget_share", "plan_limit",
  "ai_disabled", "ai_budget", "ai_unavailable", "messaging_window_closed", "private_reply_window", "already_replied", "handoff_keyword",
  "handoff_intent", "handoff_category", "handoff_negative", "handoff_model", "guardrails", "conversation_off", "no_agent", "not_applicable",
] as const;
export const AgentSkipReason = z.enum(AGENT_SKIP_REASONS);
export const KnowledgeSnippet = z.object({
  dataset: AgentDataset,
  text: z.string(),
  /** Document title (brand docs) or item kind (content). */
  source: z.string().nullable(),
  score: z.number(),
});
export const AgentRun = z.object({
  id: Id,
  agentId: Id,
  agentVersion: z.number().int(),
  surface: AgentSurface,
  trigger: z.enum(["auto", "rule", "manual", "playground", "batch"]),
  status: AgentRunStatus,
  reason: AgentSkipReason.nullable(),
  detail: z.string().nullable(),
  target: z.object({ type: z.enum(["comment", "conversation"]), id: Id, preview: z.string() }).nullable(),
  /** The reply (draft or sent). */
  text: z.string().nullable(),
  approvalId: Id.nullable(),
  ruleId: Id.nullable(),
  source: AgentSource.nullable(),
  tokens: z.number().nullable(),
  costUsd: z.number().nullable(),
  knowledge: z.array(KnowledgeSnippet),
  createdAt: IsoDate,
});
export const AgentRunsQuery = z.object({ status: AgentRunStatus.optional(), surface: AgentSurface.optional() });

// ---------------------------------------------------------------- playground (nothing is sent)
export const PlaygroundRequest = z.object({
  /** A saved agent, or an unsaved config (the editor's current form). */
  agentId: Id.optional(),
  agent: AgentInput.omit({ version: true, note: true }).optional(),
  surface: AgentSurface.default("comment_public"),
  message: z.string().trim().min(1).max(2000),
  /** Earlier turns of this playground chat (DM surface). */
  history: z.array(z.object({ role: z.enum(["customer", "agent"]), text: z.string().max(2000) })).max(20).default([]),
  /** Use a real recent item as context (its post caption / conversation history). */
  sample: z.object({ type: z.enum(["comment", "conversation"]), id: Id }).optional(),
  authorUsername: z.string().max(60).optional(),
}).refine((r) => !!r.agentId || !!r.agent, { message: "agentId or agent is required", path: ["agentId"] });
export const PlaygroundResult = z.object({
  decision: z.enum(["reply", "handoff", "skip"]),
  reply: z.string().nullable(),
  reason: AgentSkipReason.nullable(),
  detail: z.string().nullable(),
  knowledge: z.array(KnowledgeSnippet),
  /** Guardrail findings on the model's draft (links, phone numbers, mentions…). */
  violations: z.array(z.string()),
  /** The language the agent answered in. */
  language: z.string().nullable(),
  /** System prompt that was built (agent config → prompt), so the owner sees what the model sees. */
  prompt: z.string(),
  tokens: z.number().nullable(),
  costUsd: z.number().nullable(),
  durationMs: z.number(),
  /** Knowledge search fell back (no Dify Knowledge configured) or failed: snippets may be empty. */
  knowledgeAvailable: z.boolean(),
});
/** Real recent messages to try in the playground. */
export const AgentSample = z.object({ type: z.enum(["comment", "conversation"]), id: Id, text: z.string(), author: z.string().nullable(), at: IsoDate, accountId: Id });

// ---------------------------------------------------------------- draft with agent (inbox, comment panel, batch)
export const AgentDraftRequest = z.object({
  target: z.object({ type: z.enum(["comment", "conversation"]), id: Id }),
  /** Default: the agent that resolves for the item. */
  agentId: Id.optional(),
  /** comment_public (default for comments) / comment_private / dm (conversations). */
  surface: AgentSurface.optional(),
});
export const AgentDraftResult = z.object({
  run: AgentRun,
  /** Created approval (a draft to review) or null when skipped / handed off. */
  approvalId: Id.nullable(),
  agent: ResolvedAgent.nullable(),
});
export const AgentBatchRequest = z.object({
  targets: z.array(z.object({ type: z.enum(["comment", "conversation"]), id: Id })).min(1).max(500),
  surface: AgentSurface.optional(),
});
export const ResolveAgentQuery = z.object({ type: z.enum(["comment", "conversation", "post", "account"]), id: Id, surface: AgentSurface.optional() });
export const UpdateConversationAgent = z.object({
  /** inherit = defaults apply; assigned = `agentId`; off = nobody (manual replies only). */
  mode: z.enum(["inherit", "assigned", "off"]),
  agentId: Id.nullable().optional(),
});

// ---------------------------------------------------------------- resolution (pure; API, worker and the web mock share it)
export interface AgentCandidate {
  agentId: string;
  name: string;
  emoji: string | null;
  version: number;
  mode: z.infer<typeof AgentMode>;
  status: "active" | "paused";
}
export interface AssignmentRow {
  agentId: string;
  targetType: "channel" | "account" | "post";
  targetRef: string;
  surface: "comments" | "dms" | "all";
  label?: string | null;
}
export interface AgentResolveInput {
  surface: AgentSurfaceT;
  /** The rule action's agent, when a rule asked for one. */
  ruleAgentId?: string | null;
  ruleId?: string | null;
  ruleLabel?: string | null;
  /** DMs: the conversation's agent mode + manual assignment. */
  conversation?: { id: string; mode: "inherit" | "assigned" | "off"; agentId: string | null; label?: string | null } | null;
  postId?: string | null;
  accountId: string;
  channel: z.infer<typeof Channel>;
  assignments: readonly AssignmentRow[];
  agents: readonly AgentCandidate[];
}

/**
 * Who answers this item (first match wins): rule → conversation → post → account → channel. Paused or unknown agents
 * are skipped (the next source applies). A conversation in mode "off" resolves to null unless a rule names an agent
 * explicitly. Assignments match the surface kind (`comments` for comment surfaces, `dms` for DMs) or `all`.
 */
export function resolveAgent(i: AgentResolveInput): ResolvedAgentT | null {
  const byId = new Map(i.agents.map((a) => [a.agentId, a]));
  const kind = surfaceKind(i.surface);
  const pick = (agentId: string | null | undefined, source: AgentSourceT, sourceRef: string | null, sourceLabel: string | null): ResolvedAgentT | null => {
    const a = agentId ? byId.get(agentId) : undefined;
    if (!a || a.status !== "active") return null;
    return { agentId: a.agentId, name: a.name, emoji: a.emoji, version: a.version, mode: a.mode, source, sourceRef, sourceLabel };
  };
  const rule = pick(i.ruleAgentId, "rule", i.ruleId ?? null, i.ruleLabel ?? null);
  if (rule) return rule;
  const conv = i.conversation;
  if (conv?.mode === "off") return null;
  if (conv?.mode === "assigned") {
    const c = pick(conv.agentId, "conversation", conv.id, conv.label ?? null);
    if (c) return c;
  }
  const fits = (a: AssignmentRow) => a.surface === "all" || a.surface === kind;
  const find = (type: AssignmentRow["targetType"], ref: string | null | undefined) => {
    if (!ref) return null;
    // a surface-specific assignment beats an "all" one on the same target
    const rows = i.assignments.filter((a) => a.targetType === type && a.targetRef === ref && fits(a)).sort((a, b) => (a.surface === "all" ? 1 : 0) - (b.surface === "all" ? 1 : 0));
    for (const r of rows) {
      const hit = pick(r.agentId, type, ref, r.label ?? null);
      if (hit) return hit;
    }
    return null;
  };
  if (kind === "comments") {
    const post = find("post", i.postId);
    if (post) return post;
  }
  return find("account", i.accountId) ?? find("channel", i.channel);
}

// ---------------------------------------------------------------- types
export type AgentT = z.infer<typeof Agent>;
export type AgentInputT = z.input<typeof AgentInput>;
export type AgentVersionT = z.infer<typeof AgentVersion>;
export type AgentAssignmentT = z.infer<typeof AgentAssignment>;
export type AgentOverviewT = z.infer<typeof AgentOverview>;
export type AgentRunT = z.infer<typeof AgentRun>;
export type AgentUsageT = z.infer<typeof AgentUsage>;
export type AgentSkipReasonT = z.infer<typeof AgentSkipReason>;
export type KnowledgeSnippetT = z.infer<typeof KnowledgeSnippet>;
export type PlaygroundRequestT = z.input<typeof PlaygroundRequest>;
export type PlaygroundResultT = z.infer<typeof PlaygroundResult>;
export type AgentSampleT = z.infer<typeof AgentSample>;
export type AgentDraftResultT = z.infer<typeof AgentDraftResult>;
export type AgentLimitsT = z.infer<typeof AgentLimits>;
export type AgentWorkingHoursT = z.infer<typeof AgentWorkingHours>;
export type AgentHandoffT = z.infer<typeof AgentHandoff>;

// ---------------------------------------------------------------- routes (spread into `routes`)
const route = <P extends z.ZodTypeAny, Q extends z.ZodTypeAny, B extends z.ZodTypeAny, R extends z.ZodTypeAny>(d: RouteDef<P, Q, B, R>) => d;
const OrgP = z.object({ orgId: z.string().uuid() });
const AgentP = OrgP.extend({ agentId: z.string().uuid() });
const OkR = z.object({ ok: z.literal(true) });
const EmptyB = z.object({});

export const agentRoutes = {
  agents: route({ method: "GET", path: "/v1/orgs/:orgId/agents", params: OrgP, response: z.array(Agent), summary: "AI agents of the org (with assignment counts and this month's usage)" }),
  createAgent: route({ method: "POST", path: "/v1/orgs/:orgId/agents", params: OrgP, body: AgentInput, response: Agent, status: 201, summary: "Create an AI agent (version 1, audited)" }),
  agent: route({ method: "GET", path: "/v1/orgs/:orgId/agents/:agentId", params: AgentP, response: Agent, summary: "One agent with its usage" }),
  updateAgent: route({ method: "PUT", path: "/v1/orgs/:orgId/agents/:agentId", params: AgentP, body: AgentInput, response: Agent, summary: "Save an agent: a new version (409 on a stale version; audited)" }),
  deleteAgent: route({ method: "DELETE", path: "/v1/orgs/:orgId/agents/:agentId", params: AgentP, response: OkR, summary: "Delete an agent: its assignments go, rules using it are reported; history stays (admin, audited)" }),
  agentVersions: route({ method: "GET", path: "/v1/orgs/:orgId/agents/:agentId/versions", params: AgentP, response: z.array(AgentVersion), summary: "Every saved version, newest first" }),
  restoreAgentVersion: route({ method: "POST", path: "/v1/orgs/:orgId/agents/:agentId/versions/:version/restore", params: AgentP.extend({ version: z.coerce.number().int().min(1) }), body: EmptyB, response: Agent, summary: "Restore an older version (saved as a new version)" }),
  agentOverview: route({ method: "GET", path: "/v1/orgs/:orgId/agents/:agentId/overview", params: AgentP, response: AgentOverview, summary: "Where the agent is used: account/post/channel defaults, conversations, rules" }),
  createAgentAssignment: route({ method: "POST", path: "/v1/orgs/:orgId/agents/:agentId/assignments", params: AgentP, body: AgentAssignmentInput, response: AgentAssignment, status: 201, summary: "Make the agent the default of a channel, an account or a post (replaces the previous one there; audited)" }),
  deleteAgentAssignment: route({ method: "DELETE", path: "/v1/orgs/:orgId/agents/:agentId/assignments/:assignmentId", params: AgentP.extend({ assignmentId: z.string().uuid() }), response: OkR, summary: "Remove a default assignment (audited)" }),
  agentAssignments: route({ method: "GET", path: "/v1/orgs/:orgId/agent-assignments", params: OrgP, response: z.array(AgentAssignment), summary: "Every default assignment of the org (channel, account, post)" }),
  agentUsage: route({ method: "GET", path: "/v1/orgs/:orgId/agents/:agentId/usage", params: AgentP, response: AgentUsage, summary: "Replies today / this month, drafts pending, tokens, cost, budget and limits" }),
  agentRuns: route({ method: "GET", path: "/v1/orgs/:orgId/agents/:agentId/runs", params: AgentP, query: PageQuery.merge(AgentRunsQuery), response: Page(AgentRun), summary: "Activity log: every draft, send, skip (with the reason) and handoff" }),
  agentPlayground: route({ method: "POST", path: "/v1/orgs/:orgId/agents/playground", params: OrgP, body: PlaygroundRequest, response: PlaygroundResult, summary: "Try an agent (saved or unsaved) on a sample or real message: the draft and the knowledge snippets, nothing is sent" }),
  agentSamples: route({ method: "GET", path: "/v1/orgs/:orgId/agents/samples", params: OrgP, query: z.object({ surface: AgentSurface.optional(), accountId: z.string().uuid().optional() }), response: z.array(AgentSample), summary: "Recent real comments / DMs to try in the playground" }),
  resolveAgent: route({ method: "GET", path: "/v1/orgs/:orgId/agents/resolve", params: OrgP, query: ResolveAgentQuery, response: z.object({ agent: ResolvedAgent.nullable() }), summary: "Which agent handles this item and why (resolution order: rule, conversation, post, account, channel)" }),
  agentDraft: route({ method: "POST", path: "/v1/orgs/:orgId/agents/draft", params: OrgP, body: AgentDraftRequest, response: AgentDraftResult, status: 201, summary: "Draft a reply with an agent for one comment or conversation (creates an approval; never sends)" }),
  agentBatch: route({ method: "POST", path: "/v1/orgs/:orgId/agents/:agentId/batch", params: AgentP, body: AgentBatchRequest, response: z.object({ jobId: Id }), status: 202, summary: "Draft agent replies for many items as one job (each draft is an approval)" }),
  updateConversationAgent: route({ method: "PUT", path: "/v1/orgs/:orgId/conversations/:conversationId/agent", params: OrgP.extend({ conversationId: z.string().uuid() }), body: UpdateConversationAgent, response: z.object({ agent: ConversationAgent }), summary: "Assign / unassign the conversation's agent or turn AI off for it (audited)" }),
} as const;
